<template>
  <div class="editProfile">
    <div v-if="error" class="error">
      <span>{{ $t('Web.profile_error_change_profile_failed') }}</span>
    </div>

    <div v-else class="editProfile__container">
      <div class="editProfile__wrapper">
        <h3 class="editProfile__wrapper-title">Account</h3>
        <div class="editProfile__wrapper-label half left first box">
          <label for="first_name">
            <span>{{ $t('Web.EditProfile.FirstName') }}</span>
            <input
              class="form-control"
              id="first_name"
              name="first_name"
              type="text"
              v-model="profile.first_name"
              v-validate="'required'"
            />
          </label>
        </div>
        <div class="editProfile__wrapper-label half right first box">
          <label for="last_name">
            <span>{{ $t('Web.EditProfile.LastName') }}</span>
            <input
              id="last_name"
              name="last_name"
              type="text"
              v-model="profile.last_name"
              v-validate="'required'"
            />
          </label>
        </div>
        <!-- NEW FIELDS -->
        <div class="editProfile__wrapper-middle box">
          <label for="country">
            <span>{{ $t('Web.EditProfile.Country') }}</span>
            <select id="country" name="country" @change="updateCountry" v-validate="'required'">
              <option
                :selected="profile.country === key"
                v-for="(option, key) in countries"
                :key="key"
                :value="key"
                >{{ option.name }}</option
              >
            </select>
          </label>
        </div>
        <div class="editProfile__wrapper-middle box">
          <label for="phone_number">
            <span>{{ $t('Web.EditProfile.PhoneNumber') }}</span>
            <input
              id="phone_number"
              name="phone_number"
              type="text"
              v-model="profile.phone_number"
              v-validate="'required'"
              @input="phoneValidator"
            />
          </label>
        </div>
        <div class="editProfile__wrapper-middle box">
          <label for="street">
            <span>{{ $t('Web.EditProfile.Street') }}</span>
            <input
              id="street"
              name="street"
              type="text"
              v-model="profile.street"
              v-validate="'required'"
            />
          </label>
        </div>
        <div class="editProfile__wrapper-input one-by-three left last box">
          <label for="postal_code">
            <span>{{ $t('Web.EditProfile.PostalCode') }}</span>
            <input
              id="postal_code"
              name="postal_code"
              type="text"
              v-model="profile.postal_code"
              v-validate="'required'"
            />
          </label>
        </div>
        <div class="editProfile__wrapper-input two-by-three right last box">
          <label for="city">
            <span>{{ $t('Web.EditProfile.Town') }}</span>
            <input
              id="city"
              name="city"
              type="text"
              v-model="profile.city"
              v-validate="'required'"
            />
          </label>
        </div>
      </div>
      <!-- NEW FIELDS END -->

      <div class="editProfile__wrapper">
        <div class="editProfile__buttons">
          <button class="editProfile__buttons-items" @click="profileUpdate">
            {{ $t('Web.Buttons.Save') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import countries from '../../helpers/countries';

export default {
  name: 'editProfile',
  data() {
    return {
      changePassword: false,
      error: '',
      deviceZip: '',
      deviceStreet: '',
      firstName: '',
      lastName: '',
      countries
    };
  },
  methods: {
    ...mapActions(['updateProfile', 'submitAddressToDevice']),
    updateCountry(event) {
      this.profile.country = event.target.value;

      const availablePhoneCodes = Object.values(this.countries).reduce((accumulator, val) => {
        accumulator.push(val.phone_code);
        return accumulator;
      }, []);

      // not empty and different from any contry code
      if (!this.profile.phone_number || availablePhoneCodes.includes(this.profile.phone_number)) {
        this.profile.phone_number = this.countries[this.profile.country].phone_code;
      }
    },
    phoneValidator(event) {
      if (!event.target.value) {
        this.profile.phone_number = this.countries[this.profile.country].phone_code;
      }

      this.profile.phone_number = String(this.profile.phone_number).trim();
    },
    switchEditProfileContent() {
      this.changePassword = !this.changePassword;
    },
    profileUpdate() {
      this.$showGlobalLoadingSpinner();

      const newAddress = {
        profile: {
          zip: this.deviceZip || this.zipCode,
          street: this.deviceStreet || this.streetName
        },
        serial: this.device.serial
      };

      // It is possible to update the profile, without updating the device's location.
      // This is because we show both entities in one single screen, although they are independent
      // of each other. Another scenario is that only non-technicians are allowed to update a devices location.
      if (newAddress.profile.zip && newAddress.profile.street && !this.isTechnician) {
        Promise.all([
          this.updateProfile(this.$sanitize(JSON.stringify(this.profile))),
          this.submitAddressToDevice(newAddress)
        ])
          .then(() => {
            this.$parent.$emit('profileWasSaved');
          })
          .catch(err => {
            this.error = err;
          })
          .finally(() => {
            this.$hideGlobalLoadingSpinner();
          });
      } else {
        Promise.all([this.updateProfile(this.$sanitize(JSON.stringify(this.profile)))])
          .then(() => {
            this.$parent.$emit('profileWasSaved');
          })
          .catch(err => {
            this.error = err;
          })
          .finally(() => {
            this.$hideGlobalLoadingSpinner();
          });
      }
    }
  },
  computed: {
    ...mapGetters(['profile', 'device', 'isTechnician']),

    zipCode: {
      get() {
        return this.deviceZip ? this.deviceZip : ((this.device || {}).profile || {}).zip;
      },
      set(newVal) {
        this.deviceZip = newVal;
      }
    },

    streetName: {
      get() {
        return this.deviceStreet ? this.deviceStreet : ((this.device || {}).profile || {}).street;
      },
      set(newVal) {
        this.deviceStreet = newVal;
      }
    },

    profileFirstName: {
      get() {
        return (this.profile || {}).first_name || '';
      },
      set(newVal) {
        this.firstName = newVal;
      }
    },

    profileLastName: {
      get() {
        return (this.profile || {}).last_name || '';
      },
      set(newVal) {
        this.lastName = newVal;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.editProfile {
  margin: 0 auto;
  width: $maxWidth;

  h1 {
    text-align: center;
  }

  &__wrapper {
    margin-bottom: 24px;

    &-title {
      display: block;
      color: $greeny-blue;
      @include textSmall;
      font-weight: 700;
      margin: 5px 10px;
    }

    &-label {
      border-bottom-left-radius: 0px !important;
      border-bottom-right-radius: 0px !important;
      border-bottom: none !important;
    }

    &-middle {
      border-radius: 0px !important;
      border-bottom: none !important;
    }

    &-input {
      border-top-left-radius: 0px !important;
      border-top-right-radius: 0px !important;
    }

    &-label,
    &-input,
    &-middle {
      width: 88%;
      display: inline-block;

      span {
        @include textCharts;
        color: #879baa;
        display: block;
      }

      input,
      select {
        margin: 10px 0 0;
        width: 85%;
        border: none;
        line-height: 1;
        @include textCharts;
        color: #00646e;
        padding-left: 18px;
        padding-bottom: 4px;
      }

      &.half {
        width: 38.33%;

        input[type='email'],
        input[type='text'],
        input[type='password'],
        select {
          margin-left: 13px;
        }

        &.left {
          &.first {
            border-top-right-radius: 0;
          }
        }

        &.right {
          border-left: 0;

          &.first {
            border-top-left-radius: 0;
          }
        }
      }

      &.two-by-three {
        width: 50%;

        input[type='email'],
        input[type='text'],
        input[type='password'],
        select {
          margin-left: 3px;
          border-radius: 8px;
        }

        &.right {
          border-left: 0;

          &.last {
            border-bottom-left-radius: 0;
          }
        }
      }

      &.one-by-three {
        width: 26.66%;

        input[type='email'],
        input[type='text'],
        input[type='password'],
        select {
          margin-left: 3px;
          border-radius: 8px;
        }

        &.left {
          &.last {
            border-bottom-left-radius: 8px;
            border-bottom-right-radius: 0;
          }
        }
      }
    }
  }

  &__buttons {
    width: 80%;
    margin: 20px auto;

    &-items {
      height: 48px;
      &-text {
        background: none;
        color: $greeny-blue;
        text-decoration: none;
      }
      &-icon {
        background: none;
        width: auto;
      }
    }

    a[href] {
      text-align: center;
      display: block;
      margin: 28px 0 0 0;
    }
  }

  button:disabled {
    opacity: 0.5;
  }
}

.box {
  border-radius: 8px;
  border: 1px solid $cloudy-blue;
  box-shadow: 0 2px 8px 0 rgba(0, 0, 0, 0.1);
  height: 40px;
  padding: 20px;
}
</style>
